// Sprint PKG-VIZ A1 (#792) — force-directed layout helper.
//
// The issue calls for "React Flow's d3-zoom-based default... seeded for
// reproducibility within a session" — but no d3-force dependency was
// adjudicated (Q8 locks deps to exactly @xyflow/react + dagre), so this
// is a small dependency-free Fruchterman-Reingold-style simulation.
// Seeding is deterministic (sorted-id circular start position, not
// Math.random()) and the simulation itself has no randomness, so —
// like dagre/radial — identical [nodes, edges] input always produces
// identical output. Tuned for readability at 50-100 nodes per the issue.

import type { PKGNode, PKGEdge } from '@blueprint/shared';
import type { PKGLayoutPositions } from './types.ts';

const ITERATIONS = 120;
const IDEAL_EDGE_LENGTH = 160;
const REPULSION_STRENGTH = 12000;
const INITIAL_RADIUS = 300;
const DAMPING = 0.85;

interface Point {
  x: number;
  y: number;
}

export function computeForceLayout(nodes: PKGNode[], edges: PKGEdge[]): PKGLayoutPositions {
  const positions: PKGLayoutPositions = new Map();
  if (nodes.length === 0) return positions;

  // Deterministic seed: place nodes evenly around a circle in sorted-id
  // order, so re-ordering the input array doesn't change the result.
  const sortedIds = [...nodes].map(n => n.id).sort();
  const points = new Map<string, Point>();
  sortedIds.forEach((id, index) => {
    const angle = (2 * Math.PI * index) / sortedIds.length;
    points.set(id, {
      x: INITIAL_RADIUS * Math.cos(angle),
      y: INITIAL_RADIUS * Math.sin(angle),
    });
  });

  const relevantEdges = edges.filter(e => points.has(e.source) && points.has(e.target));

  let velocity = new Map<string, Point>(sortedIds.map(id => [id, { x: 0, y: 0 }]));

  for (let iter = 0; iter < ITERATIONS; iter++) {
    const forces = new Map<string, Point>(sortedIds.map(id => [id, { x: 0, y: 0 }]));

    // Repulsion — every pair of nodes pushes apart (Coulomb-like).
    for (let i = 0; i < sortedIds.length; i++) {
      for (let j = i + 1; j < sortedIds.length; j++) {
        const a = sortedIds[i];
        const b = sortedIds[j];
        const pa = points.get(a)!;
        const pb = points.get(b)!;
        const dx = pa.x - pb.x;
        const dy = pa.y - pb.y;
        const distSq = Math.max(dx * dx + dy * dy, 0.01);
        const dist = Math.sqrt(distSq);
        const force = REPULSION_STRENGTH / distSq;
        const fx = (dx / dist) * force;
        const fy = (dy / dist) * force;
        forces.get(a)!.x += fx;
        forces.get(a)!.y += fy;
        forces.get(b)!.x -= fx;
        forces.get(b)!.y -= fy;
      }
    }

    // Attraction — edges pull their endpoints toward the ideal length.
    for (const edge of relevantEdges) {
      const pa = points.get(edge.source)!;
      const pb = points.get(edge.target)!;
      const dx = pb.x - pa.x;
      const dy = pb.y - pa.y;
      const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 0.01);
      const displacement = dist - IDEAL_EDGE_LENGTH;
      const fx = (dx / dist) * displacement * 0.1;
      const fy = (dy / dist) * displacement * 0.1;
      forces.get(edge.source)!.x += fx;
      forces.get(edge.source)!.y += fy;
      forces.get(edge.target)!.x -= fx;
      forces.get(edge.target)!.y -= fy;
    }

    const nextVelocity = new Map<string, Point>();
    for (const id of sortedIds) {
      const v = velocity.get(id)!;
      const f = forces.get(id)!;
      const nv = { x: (v.x + f.x) * DAMPING, y: (v.y + f.y) * DAMPING };
      nextVelocity.set(id, nv);
      const p = points.get(id)!;
      points.set(id, { x: p.x + nv.x * 0.01, y: p.y + nv.y * 0.01 });
    }
    velocity = nextVelocity;
  }

  for (const id of sortedIds) positions.set(id, points.get(id)!);
  return positions;
}
